import React, { useState, useEffect } from 'react';

const NoTrafficClientsHistory = ({ recordId, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadHistory();
  }, [recordId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/no-traffic-clients/${recordId}/history`);
      
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      } else {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error al cargar historial:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (actionType) => {
    const icons = {
      'created': '➕',
      'contact_attempted': '📞',
      'email_sent': '📧',
      'meeting_scheduled': '📅',
      'follow_up': '🔔',
      'comment_added': '💬',
      'status_changed': '🔄',
      'escalated': '⬆️',
      'resolved': '✅',
      'updated': '✏️',
      'deleted': '🗑️'
    };
    return icons[actionType] || '📝';
  };

  const getActionLabel = (actionType) => {
    const labels = {
      'created': 'Registro Creado',
      'contact_attempted': 'Intento de Contacto',
      'email_sent': 'Email Enviado',
      'meeting_scheduled': 'Reunión Programada',
      'follow_up': 'Seguimiento',
      'comment_added': 'Comentario Agregado',
      'status_changed': 'Estado Cambiado',
      'escalated': 'Escalado',
      'resolved': 'Resuelto',
      'updated': 'Actualizado',
      'deleted': 'Eliminado'
    };
    return labels[actionType] || 'Acción';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Fecha no disponible';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);
      
      if (diffInSeconds < 60) return 'hace unos segundos';
      if (diffInSeconds < 3600) return `hace ${Math.floor(diffInSeconds / 60)} minutos`;
      if (diffInSeconds < 86400) return `hace ${Math.floor(diffInSeconds / 3600)} horas`;
      if (diffInSeconds < 2592000) return `hace ${Math.floor(diffInSeconds / 86400)} días`;
      return `hace ${Math.floor(diffInSeconds / 2592000)} meses`;
    } catch (error) {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
            <p className="text-lg text-gray-600">Cargando historial...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          <div className="p-8 text-center">
            <div className="text-6xl mb-4">❌</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Error al cargar historial</h3>
            <p className="text-gray-500 mb-4">{error}</p>
            <div className="flex justify-center space-x-3">
              <button
                onClick={loadHistory}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Reintentar
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">📋 Historial de Actividades</h2>
              <p className="text-red-100 mt-1">
                Registro completo de acciones realizadas
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {history.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Sin historial disponible
              </h3>
              <p className="text-gray-500">
                No hay actividades registradas para este cliente.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Timeline */}
              <div className="relative">
                {/* Línea vertical del timeline */}
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                
                {history.map((entry, index) => (
                  <div key={entry.id} className="relative flex items-start space-x-4 pb-8">
                    {/* Icono del timeline */}
                    <div className="relative z-10 flex items-center justify-center w-16 h-16 bg-white border-4 border-red-200 rounded-full shadow-lg">
                      <span className="text-2xl">{getActionIcon(entry.action_type)}</span>
                    </div>
                    
                    {/* Contenido */}
                    <div className="flex-1 min-w-0">
                      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
                        {/* Header de la entrada */}
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {getActionLabel(entry.action_type)}
                            </h4>
                            <div className="flex items-center space-x-2 text-sm text-gray-500 mt-1">
                              <span>👤 {entry.user_name || 'Sistema'}</span>
                              <span>•</span>
                              <span>📅 {formatDate(entry.created_at)}</span>
                              <span>•</span>
                              <span className="text-gray-400">{getTimeAgo(entry.created_at)}</span>
                            </div>
                          </div>
                          
                          {/* Badge del tipo de acción */}
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {entry.action_type}
                          </span>
                        </div>
                        
                        {/* Descripción */}
                        {entry.description && (
                          <div className="mb-4">
                            <p className="text-gray-700 leading-relaxed">
                              {entry.description}
                            </p>
                          </div>
                        )}
                        
                        {/* Detalles adicionales */}
                        {entry.details && (
                          <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-red-400">
                            <h5 className="text-sm font-semibold text-gray-900 mb-2">
                              📝 Detalles:
                            </h5>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {entry.details}
                            </p>
                          </div>
                        )}
                        
                        {/* Cambios de estado */}
                        {entry.old_status && entry.new_status && (
                          <div className="mt-4 flex items-center space-x-2 text-sm">
                            <span className="text-gray-500">Estado:</span>
                            <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">
                              {entry.old_status}
                            </span>
                            <span className="text-gray-400">→</span>
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                              {entry.new_status}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {history.length > 0 && (
                <>Total de actividades: {history.length}</>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-lg hover:from-red-700 hover:to-orange-700 font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoTrafficClientsHistory;
